import Axios, { AxiosError, AxiosRequestConfig } from "axios";
import firebase from "firebase/compat/app";

export class Token {
  static token = "";
}

interface ServerCallResponse<T> {
  data: T;
}

class ServerCall {
  private refreshAttempts = 0;
  private maxRefreshAttempts = 3;

  private axiosInstance = Axios.create();

  constructor() {
    this.axiosInstance.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config;

        if (
          error.response?.status === 401 &&
          error.response?.headers["x-token-expired"] === "true" &&
          originalRequest
        ) {
          if (this.refreshAttempts < this.maxRefreshAttempts) {
            this.refreshAttempts++;
            try {
              await this.refreshToken();
              if (originalRequest && originalRequest.headers) {
                originalRequest.headers.set?.(
                  "Authorization",
                  "Bearer " + Token.token
                );
              }
              return this.axiosInstance(originalRequest);
            } catch (refreshError) {
              this.logoutUser();
              return Promise.reject(refreshError);
            }
          } else {
            this.logoutUser();
          }
        }

        return Promise.reject(error);
      }
    );
  }

  private async refreshToken(): Promise<void> {
    try {
      const idToken = await firebase.auth().currentUser?.getIdToken();
      if (!idToken) throw new Error("Token fetch failed");
      Token.token = idToken;
      this.refreshAttempts = 0;
    } catch (error) {
      console.error("Token refresh failed", error);
      this.refreshAttempts = 0;
      throw error;
    }
  }

  private logoutUser() {
    firebase
      .auth()
      .signOut()
      .then(() => {
        console.error(
          "User logged out due to repeated token refresh failures."
        );
        window.location.href = "/login";
      });
  }

  private getAuthHeaders() {
    return {
      Authorization: "Bearer " + Token.token,
      "Access-Control-Allow-Origin": "*",
      "Access-Control-Allow-Methods": "GET,PUT,POST,DELETE,PATCH,OPTIONS",
    };
  }

  async get<T>(url: string): Promise<T> {
    console.log("GET: " + url);
    const config: AxiosRequestConfig = { headers: this.getAuthHeaders() };
    const response = await this.axiosInstance.get<ServerCallResponse<T>>(
      url,
      config
    );
    return response.data as any;
  }

  async post<T>(url: string, payload: any): Promise<T> {
    console.log("POST: " + url);
    const config: AxiosRequestConfig = { headers: this.getAuthHeaders() };
    const response = await this.axiosInstance.post<ServerCallResponse<T>>(
      url,
      payload,
      config
    );
    return response.data as any;
  }

  async put<T>(url: string, payload: any): Promise<T> {
    console.log("PUT: " + url);
    const config: AxiosRequestConfig = { headers: this.getAuthHeaders() };
    const response = await this.axiosInstance.put<ServerCallResponse<T>>(
      url,
      payload,
      config
    );
    return response.data as any;
  }

  async delete<T>(url: string): Promise<T> {
    console.log("DELETE: " + url);
    const config: AxiosRequestConfig = { headers: this.getAuthHeaders() };
    const response = await this.axiosInstance.delete<ServerCallResponse<T>>(
      url,
      config
    );
    return response.data as any;
  }

  async upload(url: string, file: File): Promise<any> {
    console.log("UPLOAD: " + url);
    const formData = new FormData();
    formData.append("file", file);

    const config: AxiosRequestConfig = {
      headers: {
        ...this.getAuthHeaders(),
        "Content-Type": "multipart/form-data",
      },
    };

    const response = await this.axiosInstance.post<ServerCallResponse<any>>(
      url,
      formData,
      config
    );
    return response.data as any;
  }

  async download(url: string, fileName: string): Promise<void> {
    console.log("DOWNLOAD: " + url);
    const response = await this.axiosInstance.get(url, {
      method: "GET",
      responseType: "blob",
      headers: this.getAuthHeaders(),
    });

    const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = blobUrl;
    link.setAttribute("download", response.headers.filename || fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
  }
}

export default new ServerCall();
